"use client";

import * as React from "react";
import {
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from "motion/react";

import { cn } from "@/lib/utils";

interface LinearAiBeamProps {
  className?: string;
  duration?: number;
}

interface MovingBorderProps {
  children: React.ReactNode;
  duration?: number;
  rx?: string;
  ry?: string;
}

function MovingBorder({
  children,
  duration = 3000,
  rx = "30%",
  ry = "30%",
}: MovingBorderProps) {
  const containerRef = React.useRef<HTMLSpanElement>(null);
  const pathRef = React.useRef<SVGRectElement>(null);
  const progress = useMotionValue(0);
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength?.();
    if (!length) return;

    const pixelsPerMillisecond = length / duration;
    progress.set((time * pixelsPerMillisecond) % length);
  });

  const x = useTransform(progress, (value) => {
    const point = pathRef.current?.getPointAtLength?.(value);
    return point?.x ?? 0;
  });
  const y = useTransform(progress, (value) => {
    const point = pathRef.current?.getPointAtLength?.(value);
    return point?.y ?? 0;
  });
  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <span ref={containerRef} className="absolute inset-0">
      <svg
        aria-hidden="true"
        className="absolute h-full w-full"
        height="100%"
        preserveAspectRatio="none"
        width="100%"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          ref={pathRef}
          fill="none"
          height={Math.max(size.height, 1)}
          rx={rx}
          ry={ry}
          width={Math.max(size.width, 1)}
          x="0"
          y="0"
        />
      </svg>
      <motion.span
        className="absolute left-0 top-0 inline-block"
        style={{ transform }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function LinearAiBeam({
  className,
  duration = 6400,
}: LinearAiBeamProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 z-[1] rounded-[inherit] border border-transparent",
        "mask-[linear-gradient(transparent,transparent),linear-gradient(#000,#000)] mask-intersect [mask-clip:padding-box,border-box]",
        className,
      )}
      data-slot="linear-ai-beam"
    >
      <MovingBorder duration={duration}>
        <span
          className="block size-16 bg-[radial-gradient(circle,#ffe0a8_0%,#ffaf53_34%,rgb(255_175_83/0.42)_56%,transparent_72%)] opacity-90 blur-[1px]"
          data-slot="linear-ai-beam-track"
        />
      </MovingBorder>
    </span>
  );
}
